import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import moment from "moment";
import DataTable from "react-data-table-component";
import { DISPLAY_DATE_FORMAT } from "../../../constants";
import PacmanWrapper from "../../../components/dashboard/PacmanWrapper";
import GuestInformationModal from "./GuestInformationModal";
import { RESERVATIONS_TAB_LIST } from "./ReservationsConsole";

// A simple debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ReservationsGuests = ({ store_id, api, onGuestReservationClick }) => {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showGuestModal, setShowGuestModal] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  useEffect(() => {
    const fetchGuests = async () => {
      if (!store_id) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: currentPage,
          limit: rowsPerPage,
          search: debouncedSearchTerm,
        }).toString();

        const { data } = await api.get(
          `/customer/store/${store_id}/guests?${params}`,
        );
        setGuests(data.guests || []);
        setTotalRows(data.pagination.total || 0);
      } catch (error) {
        setGuests([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchGuests();
  }, [store_id, api, currentPage, debouncedSearchTerm, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = async (newRowsPerPage, page) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(page);
  };

  const handleRowClicked = (row) => {
    setSelectedGuest(row);
    setShowGuestModal(true);
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => `${row.first_name} ${row.last_name}`,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
    },
    {
      name: "Total Visits",
      selector: (row) => row.total_visits,
      sortable: true,
      center: true,
    },
    {
      name: "Last Visit",
      selector: (row) => row.last_visit,
      sortable: true,
      format: (row) =>
        row.last_visit
          ? moment(row.last_visit).format(DISPLAY_DATE_FORMAT)
          : "N/A",
    },
    {
      name: "Tags",
      cell: (row) =>
        (row.tags || []).map((tag) => (
          <span key={tag} className="badge badge-info mr-1">
            {tag}
          </span>
        )),
    },
  ];

  return (
    <>
      <div className="d-flex flex-column p-4 w-100">
        <DataTable
          columns={columns}
          data={guests}
          progressPending={loading}
          progressComponent={<PacmanWrapper />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handleRowsPerPageChange}
          onChangePage={handlePageChange}
          onRowClicked={handleRowClicked}
          subHeader
          subHeaderComponent={
            <div className="d-flex justify-content-between align-items-center w-100">
              <h3>Guests</h3>
              <Form.Control
                type="text"
                placeholder="Search guests by name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-25"
              />
            </div>
          }
          striped
          highlightOnHover
          responsive
          pointerOnHover
        />
      </div>
      {selectedGuest && (
        <GuestInformationModal
          show={showGuestModal}
          onHide={() => setShowGuestModal(false)}
          guestId={selectedGuest._id}
          api={api}
          onReservationClick={onGuestReservationClick}
        />
      )}
    </>
  );
};

export default ReservationsGuests;
